import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dpsCabinetDocs, dpsPrivApi, dpsRegDoc, dpsSubmissions, eusignAssets,
  type DpsCabinetDoc, type DpsRegDoc,
  type DpsSubmission,
} from '@/api/dpsSubmissions';
import { dpsRequests } from '@/api/dpsRequests';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import { vatDeclarations } from '@/api/vatDeclarations';
import { singleTaxDeclarations } from '@/api/singleTaxDeclarations';
import { financialStatements } from '@/api/financialStatements';
import { profitTaxDeclarations } from '@/api/profitTaxDeclarations';
import { disabilityContributionReports } from '@/api/disabilityContributionReports';
import { taxReportJ05 } from '@/api/taxReportJ05';
import { erpnAppeals } from '@/api/erpnAppeals';
import { hireNotifications } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { attachments, type Attachment } from '@/api/attachments';
import type { ColDef } from 'ag-grid-community';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import DatePopover from '@/components/DatePopover';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import DpsReceiptBlank from '@/shared/eusign/DpsReceiptBlank';
import { useDefaults } from '@/store/defaults';
import {
  buildSignedContainer, bytesToBase64, developReceiptToXml,
  pickKeyForCode,
  encryptReportIdVariants, listKeys, makerIdCandidates,
  makerIdDiagnostics, parseReceiptFieldsFromXml,
  signApiAuthHeader, signOAuthPassword,
} from '@/shared/eusign/eusign';
import type {
  KeyOwnerInfo, ReceiptFields, StoredKey,
} from '@/shared/eusign/eusign';

/**
 * Кабінет ДПС — єдиний журнал відправок електронних документів у ДПС
 * (розділ «Звітність»). Тут же отримуються квитанції №2: «авторизація» ключем
 * КЕП (читається у браузері на сесію) → кнопка «оновити статус» шифрує id
 * пакета ключем і тягне рішення органу. Текст квитанції розшифровується тут.
 * Документ показує лише статуси; всі дії з квитанціями — у цьому журналі.
 */

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** Рядок журналу «Отримати ПН» (tax.erpn_fetch_log). */
interface ErpnLogRow {
  id: string;
  date_from: string;
  date_to: string;
  status: 'OK' | 'ERROR';
  fetched: number;
  created: number;
  matched: number;
  skipped_existing: number;
  rk_to_sign?: number;
  issued_updated?: number;
  error_text: string | null;
  created_at: string;
  viewed_at?: string | null;
}

const STATUS: Record<string, { text: string; color: string }> = {
  DRAFT: { text: 'чернетка', color: 'var(--text-secondary)' },
  SIGNED: { text: 'підписано', color: '#7c3aed' },
  SENT: { text: 'надіслано до ДПС', color: 'var(--badge-blue-fg)' },
  DELIVERED: { text: 'прийнято ДПС', color: '#0369a1' },
  ACCEPTED: { text: 'звіт прийнято', color: '#16a34a' },
  REJECTED: { text: 'не прийнято ДПС', color: '#dc2626' },
  ERROR: { text: 'помилка відправки', color: '#dc2626' },
};

/** Стан подачі словами власника (2026-08-18). Приймання ДВОЕТАПНЕ:
 *  кв.№1 — приймальня («прийнято ДПС» / «не прийнято ДПС»),
 *  кв.№2 — рішення органу («звіт прийнято» / «звіт не прийнято»).
 *  Поки прийшла лише попередня .KVT — «надіслано до ДПС». */
function subState(r: DpsSubmission): { text: string; color: string } {
  const hasKvt2 = !!(r.kvt2_base64 || r.receipt2_xml);
  if (r.status === 'ACCEPTED') return { text: 'звіт прийнято', color: '#16a34a' };
  if (r.status === 'REJECTED') {
    return hasKvt2
      ? { text: 'звіт не прийнято', color: '#dc2626' }
      : { text: 'не прийнято ДПС', color: '#dc2626' };
  }
  return STATUS[r.status] ?? { text: r.status, color: 'var(--text-secondary)' };
}

// Людська назва форми звіту за кодом (reg_doc). Квитанції — «Квитанція».
const REG_DOC_NAMES: Record<string, string> = {
  J0500110: 'Податковий розрахунок (ПДФО/ВЗ/ЄСВ)',
  J0200120: 'Декларація з ПДВ',
  J0100120: 'Декларація з податку на прибуток',
  J0100130: 'Декларація з податку на прибуток',
  J3000512: 'Об’єднана звітність ЄСВ',
  BOTB0501: 'Податкові зобов’язання майбутніх періодів',
  BOTB0101: 'Податковий борг',
  J1400306: 'Довідка про відсутність заборгованості з платежів',
  J1400207: 'Витяг з ІКС ДПС про стан розрахунків платника (з пенею)',
  J1400208: 'Витяг про стан розрахунків з бюджетом',
  J1420401: 'Витяг про стан розрахунків з бюджетом',
  J1430001: 'Відмова у погодженні Довідки-розрахунку',
  D0300201: 'Повідомлення Світового банку',
  MSG: 'Інформаційне повідомлення',
};
// Назва форми: спершу docName з API (людська назва від ДПС), інакше словник,
// інакше «Форма <код>». Квитанції — окремо.
function regDocName(r: { doc?: string | null; docName?: string | null }): string {
  const doc = r.doc ?? null;
  if (doc && /^[JF]1499/.test(doc)) {
    return doc.startsWith('J14992') || doc.startsWith('F14992')
      ? 'Квитанція №2' : 'Квитанція №1';
  }
  if (r.docName && r.docName.trim()) return r.docName.trim();
  if (doc && REG_DOC_NAMES[doc]) return REG_DOC_NAMES[doc];
  return doc ? `Форма ${doc}` : 'Звіт';
}
// Дата/час подання звіту — поле dget з API.
function regDate(r: { dget?: string | null }): string {
  return r.dget || '—';
}


const MONTHS_UA = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень',
  'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const QUARTERS_UA = ['', 'I', 'II', 'III', 'IV'];

/** Період подання, як його показує веб-кабінет ДПС: «Липень 2026» /
 * «II кв. 2026» (period_type: 1=місяць, 2=квартал). */
function periodLabel(r: { period_year?: number | null;
  period_month?: number | null; period_type?: number | null }): string {
  if (!r.period_year) return '—';
  if (r.period_type === 2) {
    const q = Math.ceil((r.period_month ?? 0) / 3);
    return q ? `${QUARTERS_UA[q]} кв. ${r.period_year}` : String(r.period_year);
  }
  const m = r.period_month ?? 0;
  return m ? `${MONTHS_UA[m]} ${r.period_year}` : String(r.period_year);
}

// Вхідні, що є ПОВІДОМЛЕННЯМ без окремого документа-файла (BOTB*, MSG) —
// показуємо текст повідомлення, а не тягнемо PDF (його немає).
// 2026-08-07 (рішення власника): людські назви частих вхідних форм.
const CDOC_NAMES: Record<string, string> = {
  J1420501: 'Витяг', F1420501: 'Витяг',
  J1412407: 'Рішення про реєстрацію/відмову у реєстрації ПН/РК',
  F1412407: 'Рішення про реєстрацію/відмову у реєстрації ПН/РК',
};


// source_type → маршрут документа-джерела (для переходу «відкрити документ»)
const SOURCE_ROUTE: Record<string, (id: string) => string> = {
  'doc.hire_notifications': (id) => `/hire-notifications/${id}`,
  'doc.dps_requests': (id) => `/dps-cabinet/requests/${id}`,
  'doc.tax_reports': (id) => `/tax-reports-j05/${id}`,
  'tax.tax_invoices_outgoing': (id) => `/tax-invoices-outgoing/${id}`,
  'doc.vat_declarations': (id) => `/taxation/vat-declarations/${id}`,
  'doc.single_tax_declarations': (id) => `/taxation/single-tax-declarations/${id}`,
  'doc.financial_statements': (id) => `/reports/financial/${id}`,  // ч.1386
  'doc.profit_tax_declarations': (id) => `/taxation/profit-tax-declarations/${id}`,  // ч.1387
  'doc.disability_contribution_reports': (id) => `/taxation/disability-contribution-reports/${id}`,
  'doc.erpn_appeals': (id) => `/erpn-appeals/${id}`,  // ч.1296
};

// source_type → як отримати XML документа для підписання (blob + filename).
const SOURCE_XML: Record<string, (id: string) => Promise<any>> = {
  'doc.hire_notifications': (id) => hireNotifications.xml(id),
  'doc.dps_requests': (id) => dpsRequests.xml(id),
  'tax.tax_invoices_outgoing': (id) => taxInvoicesOutgoing.erpnXml(id),
  'doc.vat_declarations': (id) => vatDeclarations.xml(id),
  'doc.single_tax_declarations': (id) => singleTaxDeclarations.xml(id),
  'doc.financial_statements': (id) => financialStatements.xml(id),
  'doc.profit_tax_declarations': (id) => profitTaxDeclarations.xml(id),
  'doc.disability_contribution_reports': (id) => disabilityContributionReports.xml(id),
};

// Джерела, що підписуються ПАКЕТОМ (основна форма + додатки) — кожен файл окремо.
const PACKAGE_FILES: Record<string, (id: string) => Promise<{ files: { fname: string; xml_base64: string }[] }>> = {
  'doc.tax_reports': (id) => taxReportJ05.files(id),
  'doc.vat_declarations': (id) => vatDeclarations.files(id),
  'doc.single_tax_declarations': (id) => singleTaxDeclarations.files(id),
  'doc.financial_statements': (id) => financialStatements.files(id),  // ч.1386: Ф1 + Ф2 / один файл малого
  'doc.profit_tax_declarations': (id) => profitTaxDeclarations.files(id),  // ч.1387: декларація + РІ + фінзвітність
  'doc.erpn_appeals': (id) => erpnAppeals.files(id),  // ч.1296: повідомлення + J1360102 на кожну копію
};

// Людське пояснення частих кодів приймальні ДПС (факт 2026-08-18:
// «ДПС: ERROR_DB» без пояснення = здогадки на годину).
function dpsErrorHint(status: string, message?: string | null): string {
  const m = (message ?? '').toLowerCase();
  if (status === 'ERROR_DB' && m.includes('вже було надіслано')) {
    return 'Цей НОМЕР документа в періоді вже зайнятий попередньою '
      + 'відправкою (навіть якщо ту ДПС відхилила). Змініть номер '
      + 'розрахунку на наступний і підпишіть/відправте ще раз.';
  }
  if (status === 'ERROR_DECRYPT') {
    return 'ДПС не змогла розшифрувати пакет — перевірте, що підписи '
      + 'накладено ключем цієї юрособи, і підпишіть заново.';
  }
  if (status === 'ERROR_SERTIF' || status === 'ERROR_SERTIF_ORG') {
    return 'Проблема з сертифікатом ключа (недійсний або не той власник).';
  }
  if (status === 'ERROR_XSD') {
    return 'Пакет не пройшов перевірку структури — натисніть «перевірити» '
      + 'у документі і виправте помилки; точна причина — у квитанції №1 (кнопка «кв.1»).';
  }
  return '';
}

/** Текст повідомлення кабінету (BOTB0501, D0300201 тощо) → безпечний HTML.
 *  ДПС кладе у текст посилання <a href> і переноси <br/>; усе інше
 *  екрануємо, щоб чужа розмітка/скрипти не потрапили на сторінку. */
function cabTextHtml(r: { title?: string | null; doc_name?: string | null }): string {
  const raw = (r.title || r.doc_name || '').trim();
  if (!raw) return '<p>Документ без вмісту.</p>';
  const parts: string[] = [];
  // Розбираємо на дозволені теги (<br>, <a href="http(s)://…">…</a>),
  // решту — екрануємо як текст.
  const re = /<br\s*\/?>|<a\s+[^>]*href="(https?:\/\/[^"]+)"[^>]*>([\s\S]*?)<\/a>/gi;
  const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    parts.push(esc(raw.slice(last, m.index)));
    if (m[1]) {
      parts.push(`<a href="${esc(m[1])}" target="_blank" rel="noopener noreferrer">`
        + `${esc((m[2] || '').replace(/<[^>]*>/g, ''))}</a>`);
    } else {
      parts.push('<br/>');
    }
    last = m.index + m[0].length;
  }
  parts.push(esc(raw.slice(last)));
  return `<div style="line-height:1.6">${parts.join('')}</div>`;
}

export default function DpsCabinetPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Пошук по Журналу: код форми (J0500110) і назва документа.
  const [formQ, setFormQ] = useState('');
  const [titleQ, setTitleQ] = useState('');
  const [keys, setKeys] = useState<StoredKey[]>(() => [...listKeys()]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Квитанція у попапі — друкований бланк (форма J1499202).
  const [receiptView, setReceiptView] =
    useState<{ fields: ReceiptFields; org: string } | null>(null);
  const [tab, setTab] = useState<'journal' | 'incoming' | 'reports' | 'erpn'>('journal');
  // 0432 ЄРПН: захід на вкладку помічає журнал отримання переглянутим
  // (жирність нових лишається ДО наступного заходу — БЕЗ інвалідації).
  useEffect(() => {
    if (tab === 'erpn' && orgId) {
      dpsPrivApi.erpnFetchLogViewed(orgId).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, orgId]);
  const [privBusy, setPrivBusy] = useState(false);
  // Перегляд документа кабінету у ПОПАПІ (не нове вікно — браузери блокують):
  // або PDF (blob у iframe), або HTML-текст (стан розрахунків тощо).
  const [cabView, setCabView] = useState<
    { title: string; pdfUrl?: string; html?: string; keepUrl?: boolean } | null>(null);
  // «Звітність» (reg_doc): період + список поданих звітів (тягнеться з ключем).
  const now = new Date();
  const [repYear, setRepYear] = useState(now.getFullYear());
  const [repMonth, setRepMonth] = useState(now.getMonth() + 1);
  // 2026-08-07: список звітності — зі збереженого (savedRepQ), не зі state.
  // Файли розкритого звіту (осн.+додатки+квитанції) у попапі.
  const [repFiles, setRepFiles] = useState<
    { title: string; orgId: string; year: number; items: DpsRegDoc[];
      // 0452: якір архіву «Кабінет ДПС» (рядок головного звіту) + режим
      // «з архіву» (перегляд збережених PDF без ключа).
      anchorCod?: string; archived?: Attachment[] } | null>(null);
  // Кеш «Звітності» у пам'яті вкладки — щоб не смикати ДПС повторно
  // (менше запитів = безпечніше по лімітах; F5 скидає). Ключі: список за
  // «org|рік|місяць»; файли за «org|рік|codRegdoc»; PDF за «org|рік|codRegdoc».
  const repFilesCache = useRef<Map<string, DpsRegDoc[]>>(new Map());
  const repPdfCache = useRef<Map<string, string>>(new Map());  // → blob url
  // «Вхідні» (post/incoming) — як «Звітність»: тягнемо з ДПС на вимогу, НЕ
  // зберігаємо; кеш у пам'яті вкладки. Квитанції (J1499xxx) НЕ показуємо.
  // Пагінація як у ДПС: тягнемо по одній сторінці (Spring page/last).

  // ПН з ЄРПН (2026-08-04): прямий fetch отриманих ПН → вхідні ПН у системі.
  const [erpnFrom, setErpnFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [erpnTo, setErpnTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [erpnBusy, setErpnBusy] = useState(false);
  // 2026-08-06: ЄДИНИЙ звіт синхронізації (3 кроки однією кнопкою).
  const [syncReport, setSyncReport] = useState<{
    fetched?: number; created?: number; matched?: number;
    skipped_existing?: number; created_contractors?: string[];
    unmatched?: string[]; no_contractor?: string[]; not_ours?: string[];
    b2b_total?: number; rk_to_sign_created?: number; rk_already?: number;
    rk_skipped_signed?: number; rk_signed_now?: number;
    issued_updated?: number; already_registered?: number;
    not_in_register?: string[]; register_rows_preview?: string[];
    skipped_rows?: string[];
    errors: string[];
  } | null>(null);

  // Кеш Bearer-токена автологіну (живе 600 с) — повторний fetch без
  // повторного обміну підпису на токен. Ключ: orgId.
  const erpnTokenCache = useRef<Map<string, { token: string; exp: number }>>(new Map());

  /**
   * Автологін кабінету (КЕП → Bearer, 2026-08-04): username =
   * «ЄДРПОУ-РНОКПП-makerId», password = CAdES-T підпис ЄДРПОУ. makerId —
   * дорозвідка: перебираємо кандидатів з даних сертифіката (13-значний
   * ms-epoch); перший, що дав 200 — переможець (лог у консоль).
   */
  async function erpnAutoLogin(targetOrgId: string, owner: KeyOwnerInfo,
                               code: string): Promise<string> {
    const cached = erpnTokenCache.current.get(targetOrgId);
    if (cached && cached.exp > Date.now()) return cached.token;
    const drfo = (owner.subjDRFOCode || owner.subjEDRPOUCode || '').trim();
    const candidates = makerIdCandidates();
    if (!candidates.length) {
      makerIdDiagnostics();
      throw new Error(
        'Автологін: не знайдено makerId у даних сертифіката — дамп у консолі '
        + 'браузера (F12), надішліть його розробнику.');
    }
    const password = await signOAuthPassword(code);
    let lastErr: unknown = null;
    for (const maker of candidates.slice(0, 3)) {
      try {
        const tok = await dpsPrivApi.oauthToken({
          organization_id: targetOrgId, signer_edrpou: code,
          username: `${code}-${drfo}-${maker}`, password,
        });
        // eslint-disable-next-line no-console
        console.info('[dps] автологін ОК, makerId =', maker);
        const exp = Date.now() + (Number(tok.expires_in || 600) - 60) * 1000;
        erpnTokenCache.current.set(targetOrgId, { token: tok.access_token, exp });
        return tok.access_token;
      } catch (e) {
        lastErr = e;
        // eslint-disable-next-line no-console
        console.warn('[dps] автологін: makerId-кандидат не прийнято:', maker);
      }
    }
    makerIdDiagnostics();
    const detail = (lastErr as any)?.response?.data?.detail
      ?? (lastErr as any)?.message ?? String(lastErr);
    throw new Error(
      `Автологін кабінету не вдався (${candidates.length} кандидатів makerId, `
      + `дамп у консолі браузера — надішліть розробнику). `
      + `Остання відповідь: ${detail}.`);
  }

  // Журнал запусків «ПН → у систему» (вкладка «Отримати ПН»).
  const erpnLogQ = useQuery({
    queryKey: ['erpn-fetch-log', orgId],
    queryFn: () => dpsPrivApi.erpnFetchLog(orgId),
    enabled: tab === 'erpn' && !!orgId,
  });

  // ЄДИНА синхронізація з ЄРПН (2026-08-06, рішення власника — одна
  // кнопка замість трьох): отримані ПН → у систему; непідписані РК з
  // «Реєстрація ПН-РК» → вхідні «на підпис»; статуси виданих ПН/РК.
  async function syncAll(targetOrgId: string) {
    setError(null); setSyncReport(null); setErpnBusy(true);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const token = await erpnAutoLogin(targetOrgId, owner, code);
      const rep = await dpsPrivApi.erpnSyncAll({
        auth_header: `Bearer ${token}`, organization_id: targetOrgId,
        signer_edrpou: code, date_from: erpnFrom, date_to: erpnTo,
      });
      setSyncReport(rep);
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
      qc.invalidateQueries({ queryKey: ['tax-invoices-outgoing'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setErpnBusy(false);
      qc.invalidateQueries({ queryKey: ['erpn-fetch-log'] });
    }
  }
  const incPdfCache = useRef<Map<string, string>>(new Map());  // org|year|id → blob
  const keyReady = keys.length > 0;

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Журнал — серверна пагінація/сортування (канон Норми #29, 2026-08-22):
  // фільтри статус/назва/форма/дати виконує backend (параметри запиту).
  const journalRefreshRef = useRef<(() => void) | null>(null);
  const refreshJournal = () => journalRefreshRef.current?.();
  const { datasource: journalDs, total: journalTotal } =
    useInfiniteGridDatasource<DpsSubmission>(
      (limit, offset, sort) => dpsSubmissions.list({
        organization_id: orgId || undefined,
        status: statusFilter || undefined,
        form_code: formQ.trim() || undefined,
        form_title: titleQ.trim() || undefined,
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        limit, offset,
      }),
      [orgId, statusFilter, formQ, titleQ, dateFrom, dateTo],
    );


  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data]);
  // orgId → код особи (ЄДРПОУ юрособи або РНОКПП/ІПН ФОП) для звірки з ключем.
  const orgCode = useMemo(
    () => new Map((orgList.data?.items ?? []).map(
      (o) => [o.id, (o.edrpou || o.inn || '').trim()])),
    [orgList.data]);


  // Порівняння кодів «як ДПС»: без провідних нулів (ЄДРПОУ 8 vs 10 цифр).
  /** Обрати і активувати ключ юрособи — ЄДИНА логіка з Журналом ЕДО
   *  (pickKeyForCode у shared/eusign, уніфікація власника 2026-08-17). */
  async function pickKeyForOrg(organizationId: string): Promise<KeyOwnerInfo> {
    return pickKeyForCode(
      orgCode.get(organizationId) ?? '',
      orgName.get(organizationId) ?? 'цієї юрособи');
  }

  // «Вхідні»: підписуємо ЄДРПОУ ключем → тягнемо список вхідних з ДПС (усі
  // сторінки), КВИТАНЦІЇ (J1499xxx) відкидаємо. Кеш у пам'яті — повторне
  // відкриття без запиту до ДПС (F5 скидає). Як «Звітність».
  // Одна сторінка вхідних (як пагінація ДПС): не тягнемо все одразу.
  // «Вхідні» (2026-08-07, рішення власника): ТЕКСТИ зберігаються у нас —
  // показ одразу з БД (без ключа); «оновити» = sync (лише XML/текст, БЕЗ
  // PDF); ФАЙЛ тягнеться з кабінету по кліку (кеш на сесію).
  const savedDocsQ = useQuery({
    queryKey: ['dps-cabinet-docs', orgId],
    queryFn: () => dpsCabinetDocs.list({ organization_id: orgId, limit: 500 }),
    enabled: tab === 'incoming' && !!orgId,
  });
  async function syncIncoming(targetOrgId: string) {
    setError(null); setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const rep = await dpsPrivApi.sync({
        auth_header: signApiAuthHeader(code), organization_id: targetOrgId,
        signer_edrpou: code,
      });
      qc.invalidateQueries({ queryKey: ['dps-cabinet-docs'] });
      if (rep.errors > 0) {
        setError(`Збережено ${rep.saved}, помилок ${rep.errors} — спробуйте «оновити» ще раз.`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }
  // 2026-08-07 (рішення власника): назва — ЗА НОМЕРОМ ФОРМИ (словники),
  // далі HDOCNAME з XML, далі текст. Квитанції (J/F1499*) у «Вхідних» НЕ
  // показуються (як і раніше; у квитанції HDOCNAME = назва ДЕКЛАРОВАНОЇ
  // форми — плутає, зауваження власника 2026-08-07).
  function savedName(r: DpsCabinetDoc): string {
    const c = r.cdoc ?? '';
    if (/^[JF]1499/.test(c)) {
      return c.startsWith('J14992') || c.startsWith('F14992')
        ? 'Квитанція №2' : 'Квитанція №1';
    }
    return (c ? (CDOC_NAMES[c] || REG_DOC_NAMES[c]) : undefined)
      || r.doc_name
      || (r.title ? r.title.replace(/<[^>]+>/g, ' ').trim() : '')
      || c || '—';
  }
  const savedRows = (savedDocsQ.data?.items ?? [])
    .filter((r) => !(r.cdoc && /^[JF]1499/.test(r.cdoc)));
  const [savedPage, setSavedPage] = useState(0);
  const SAVED_PER_PAGE = 10;
  // Перегляд збереженого: PDF з кабінету на вимогу (кеш blob на сесію);
  // якщо у БД лишився legacy-PDF (до 2026-08-07) — беремо його.
  async function openSavedDoc(r: DpsCabinetDoc) {
    // 0432: позначка «переглянуто» (жирність знімається).
    if (!r.viewed_at) {
      dpsCabinetDocs.markViewed(r.id)
        .then(() => qc.invalidateQueries({ queryKey: ['dps-cabinet-docs'] }))
        .catch(() => {});
    }
    if (!orgId) return;
    setError(null);
    const year = r.period_year ?? now.getFullYear();
    const cacheKey = `${orgId}|${year}|${r.dps_doc_id}`;
    const cachedUrl = incPdfCache.current.get(cacheKey);
    if (cachedUrl) {
      setCabView({ title: savedName(r), pdfUrl: cachedUrl, keepUrl: true });
      return;
    }
    // Документ БЕЗ файлу (BOTB0501 «податкові зобов'язання», BOTB0101
    // «податковий борг», D0300201 сповіщення) існує лише як ТЕКСТ у
    // списку — ДПС файлу не віддає. Раніше ми все одно йшли за PDF і
    // юзер бачив порожнє вікно (факт: Медоїд, документ 27.01.2026).
    if (r.has_body === false && (r.title || r.doc_name)) {
      setCabView({ title: savedName(r), html: cabTextHtml(r) });
      return;
    }
    setPrivBusy(true);
    try {
      if (r.has_pdf) {
        const resp = await dpsCabinetDocs.pdf(r.id);
        const url = URL.createObjectURL(
          new Blob([resp.data], { type: 'application/pdf' }));
        incPdfCache.current.set(cacheKey, url);
        setCabView({ title: savedName(r), pdfUrl: url, keepUrl: true });
        return;
      }
      // 0452 (галка org «зберігати документи Кабінету ДПС»): якщо документ
      // уже архівовано вкладенням — відкриваємо БЕЗ ключа.
      try {
        const arch = await attachments.list({ organization_id: orgId,
          document_type: 'dps.cabinet', document_id: r.id });
        const last = arch[arch.length - 1];
        if (last) {
          const blob = await attachments.downloadBlob(last.id);
          const url = URL.createObjectURL(blob);
          incPdfCache.current.set(cacheKey, url);
          setCabView({ title: savedName(r), pdfUrl: url, keepUrl: true });
          return;
        }
      } catch { /* нема права на вкладення — йдемо по ключу, як раніше */ }
      if (!keyReady) {
        setError('Спершу зчитайте ключ КЕП (угорі)');
        return;
      }
      const owner = await pickKeyForOrg(orgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const resp = await dpsPrivApi.file({
        auth_header: signApiAuthHeader(code), organization_id: orgId,
        signer_edrpou: code, year, doc_id: r.dps_doc_id, fmt: 'pdf',
      });
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: 'application/pdf' }));
      incPdfCache.current.set(cacheKey, url);
      setCabView({ title: savedName(r), pdfUrl: url, keepUrl: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // Відкрити PDF вхідного документа (тягнемо з ДПС на вимогу, кеш blob).

  // «Звітність»: завантажити список поданих звітів за період (підпис ключем).
  // «Звітність» (2026-08-07, рішення власника): ЗБЕРЕЖЕНИЙ список показуємо
  // одразу при виборі періоду (без ключа); «оновити» тягне з кабінету за
  // обраний період (перший раз/оновлення) і зберігає у нас.
  const savedRepQ = useQuery({
    queryKey: ['dps-reg-saved', orgId, repYear, repMonth],
    queryFn: () => dpsRegDoc.saved({
      organization_id: orgId, period_year: repYear, period_month: repMonth }),
    enabled: tab === 'reports' && !!orgId,
  });
  // 0434: НАШІ подачі за період. Реєстр кабінету (вище) віддає лише
  // ПРИЙНЯТУ звітність — відхилений пакет там не з'являється взагалі
  // (факт 2026-08-18), тому історію відправок показуємо зі свого журналу.
  const attemptsQ = useQuery({
    queryKey: ['dps-attempts', orgId, repYear, repMonth],
    queryFn: () => dpsRegDoc.attempts({
      organization_id: orgId, period_year: repYear, period_month: repMonth }),
    enabled: tab === 'reports' && !!orgId,
  });
  // 0437: строки дії сертифікатів ДПС — банер за 30 днів до кінця
  // (прострочений серт шифрування = усі пакети відбиваються без квитанції).
  const assetsQ = useQuery({
    queryKey: ['eusign-assets-status'],
    queryFn: () => eusignAssets.status(),
    staleTime: 60 * 60 * 1000,
  });
  const expiringCerts = (assetsQ.data?.certs ?? []).filter((c) => c.expiring);
  async function loadReports(targetOrgId: string) {
    setError(null);
    setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      await dpsRegDoc.list({
        auth_header: authHeader, organization_id: targetOrgId,
        signer_edrpou: code, period_year: repYear, period_month: repMonth,
      });
      qc.invalidateQueries({ queryKey: ['dps-reg-saved'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // Розкрити файли одного звіту (осн. + додатки + квитанції).
  async function openReportFiles(row: DpsRegDoc, targetOrgId: string) {
    setError(null);
    // 0432: позначка «переглянуто» (жирність знімається).
    if (!row._viewed_at) {
      dpsRegDoc.markViewed({ organization_id: targetOrgId,
        cod_regdoc: String(row.codRegdoc) })
        .then(() => qc.invalidateQueries({ queryKey: ['dps-reg-saved'] }))
        .catch(() => {});
    }
    const year = row.periodYear ?? repYear;
    const cacheKey = `${targetOrgId}|${year}|${row.codRegdoc}`;
    const title = `${regDocName(row)} — ${row.periodMonth ?? ''}/${year}`;
    const anchorCod = String(row.codRegdoc);
    const cached = repFilesCache.current.get(cacheKey);
    if (cached) {  // з кешу — без запиту до ДПС
      setRepFiles({ title, orgId: targetOrgId, year, items: cached, anchorCod });
      return;
    }
    // 0452: без ключа — показуємо АРХІВОВАНІ файли звіту (галка org),
    // якщо вони є; інакше підказка про ключ.
    if (!keyReady) {
      try {
        if (row._id) {
          const arch = await attachments.list({ organization_id: targetOrgId,
            document_type: 'dps.cabinet', document_id: row._id });
          if (arch.length > 0) {
            setRepFiles({ title: `${title} (з архіву)`, orgId: targetOrgId,
              year, items: [], anchorCod, archived: arch });
            return;
          }
        }
      } catch { /* нема права на вкладення */ }
      setError('Спершу зчитайте ключ КЕП (угорі)');
      return;
    }
    setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(targetOrgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const items = (await dpsRegDoc.files({
        auth_header: authHeader, organization_id: targetOrgId,
        signer_edrpou: code, year, cod_regdoc: String(row.codRegdoc),
      })) ?? [];
      repFilesCache.current.set(cacheKey, items);
      setRepFiles({ title, orgId: targetOrgId, year, items, anchorCod });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // Скачати конкретний файл звіту (PDF) з reg_doc.
  async function downloadRegDoc(row: DpsRegDoc) {
    if (!repFiles) return;
    setError(null);
    const year = row.periodYear ?? repFiles.year;
    const cacheKey = `${repFiles.orgId}|${year}|${row.codRegdoc}`;
    const cachedUrl = repPdfCache.current.get(cacheKey);
    if (cachedUrl) {  // PDF уже качали — з кешу, без запиту до ДПС
      setCabView({ title: regDocName(row), pdfUrl: cachedUrl, keepUrl: true });
      return;
    }
    setPrivBusy(true);
    try {
      const owner = await pickKeyForOrg(repFiles.orgId);
      const code = owner.subjEDRPOUCode || owner.subjDRFOCode || '';
      const authHeader = signApiAuthHeader(code);
      const resp = await dpsRegDoc.file({
        auth_header: authHeader, organization_id: repFiles.orgId,
        signer_edrpou: code, year, cod_regdoc: String(row.codRegdoc), fmt: 'pdf',
        // 0452: сервер архівує PDF під рядком ГОЛОВНОГО звіту (галка org).
        anchor_cod_regdoc: repFiles.anchorCod, file_label: regDocName(row).slice(0, 150),
      });
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: 'application/pdf' }));
      repPdfCache.current.set(cacheKey, url);
      setCabView({ title: regDocName(row), pdfUrl: url, keepUrl: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setPrivBusy(false);
    }
  }

  // «оновити статус»: шифруємо id пакета сесійним ключем → тягнемо кв.№2.
  // ДІАГНОСТИКА: точний формат encryptedId ДПС не документує — перебираємо
  // варіанти, доки відповідь не перестане бути NOT_KVT (лог на backend
  // пише повну відповідь ДПС по кожному варіанту).
  async function refresh(sub: DpsSubmission) {
    if (sub.dps_report_id == null) { setError('Пакет без ідентифікатора ДПС.'); return; }
    setBusyId(sub.id); setError(null);
    try {
      // Обрати й активувати ключ саме цієї юрособи (розшифрувати id пакета
      // можна лише ключем, на який ДПС шифрувала квитанцію).
      const owner = await pickKeyForOrg(sub.organization_id);
      const signerEdrpou = owner.subjEDRPOUCode ?? owner.subjDRFOCode;
      const variants = encryptReportIdVariants(sub.dps_report_id, sub.file_name);
      let fresh: DpsSubmission | null = null;
      let worked: string | null = null;
      for (const v of variants) {
        const encId = await v.build();
        fresh = await dpsSubmissions.refresh(sub.id, encId, signerEdrpou);
        if (fresh.kvt2_base64 || (fresh.dps_status_code
            && fresh.dps_status_code !== 'NOT_KVT')) {
          worked = v.label;
          break;
        }
      }
      if (fresh && worked) {
        console.info(`kvt_by_id: спрацював формат «${worked}»`);
        await cacheReceipts(fresh);
      } else {
        setError('ДПС відповідає NOT_KVT на всі формати запиту — імовірно, '
          + 'квитанція №2 ще не сформована на стороні ДПС. Деталі — у логах.');
      }
      refreshJournal();
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // «підписати»: XML документа → повний транспортний контейнер (ключ у
  // браузері) → зберегти на сервері (status=SIGNED). Відправка — окремо.
  // Для ПАКЕТА (doc.tax_reports) — підписати КОЖЕН файл (осн+додатки) і
  // зберегти JSON-масив [{fname, container}]; відправка — одним /report.
  async function signSub(sub: DpsSubmission) {
    setBusyId(sub.id); setError(null);
    const email = orgList.data?.items.find(
      (o) => o.id === sub.organization_id)?.email ?? '';
    try {
      // Обрати й активувати ключ саме цієї юрособи + звірити код (ЄДРПОУ/
      // РНОКПП) ДО підпису — інакше ДПС відхилить «підписано не тією особою».
      const owner = await pickKeyForOrg(sub.organization_id);
      const issuerCN = owner.issuerCN ?? '';
      const signerEdrpou = owner.subjEDRPOUCode ?? owner.subjDRFOCode;
      const getFiles = PACKAGE_FILES[sub.source_type];
      if (getFiles) {
        const { files } = await getFiles(sub.source_id);
        const pkg: { fname: string; container: string }[] = [];
        for (const f of files) {
          const bytes = Uint8Array.from(atob(f.xml_base64), (c) => c.charCodeAt(0));
          const container = await buildSignedContainer(bytes, f.fname, issuerCN, email);
          pkg.push({ fname: f.fname, container: bytesToBase64(container) });
        }
        await dpsSubmissions.sign(
          sub.id, JSON.stringify(pkg), files[0]?.fname ?? 'package', signerEdrpou);
        refreshJournal();
        qc.invalidateQueries({ queryKey: ['dps-incoming'] });
        return;
      }
      const getXml = SOURCE_XML[sub.source_type];
      if (!getXml) { setError(`Невідоме джерело ${sub.source_type}`); return; }
      const resp = await getXml(sub.source_id);
      const cd: string = resp.headers['content-disposition'] ?? '';
      const m = cd.match(/filename="([^"]+)"/);
      // Ім'я файлу — ЯК ЗГЕНЕРУВАВ backend (…2657.xml, нижнє розширення —
      // саме так ДПС пише імена у своїх квитанціях; НЕ upper-case).
      const filename = m?.[1] ?? `${sub.form_code}.xml`;
      const bytes = new Uint8Array(await (resp.data as Blob).arrayBuffer());
      const container = await buildSignedContainer(
        bytes, filename, issuerCN, email);
      await dpsSubmissions.sign(
        sub.id, bytesToBase64(container), filename, signerEdrpou);
      refreshJournal();
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Видалити чернетку (лише DRAFT/ERROR): прибирає запис журналу + сам
  // податковий розрахунок (якщо він ще не поданий). Успішні — не чіпаємо.
  async function removeDraft(sub: DpsSubmission) {
    if (sub.status !== 'DRAFT' && sub.status !== 'ERROR') return;
    if (!window.confirm(
      'Видалити чернетку та відповідний розрахунок? Дію не можна скасувати.')) {
      return;
    }
    setBusyId(sub.id); setError(null);
    try {
      await dpsSubmissions.remove(sub.id);
      refreshJournal();
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // «відправити»: шле збережений підписаний контейнер (ключ не потрібен).
  async function sendSub(sub: DpsSubmission) {
    setBusyId(sub.id); setError(null);
    try {
      const res = await dpsSubmissions.send(sub.id);
      if (res.dps_status && res.dps_status !== 'OK') {
        const hint = dpsErrorHint(res.dps_status, res.message);
        setError(`ДПС: ${res.dps_status}`
          + (res.message ? ` — ${res.message}` : '')
          + (hint ? `\n${hint}` : ''));
      }
      refreshJournal();
      qc.invalidateQueries({ queryKey: ['dps-incoming'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Розшифрований XML квитанції №num: спершу кеш (receiptN_xml, без ключа),
  // інакше розшифрувати ключем і закешувати. Повертає XML або null.
  async function receiptXml(sub: DpsSubmission, num: 1 | 2): Promise<string | null> {
    const cached = num === 1 ? sub.receipt1_xml : sub.receipt2_xml;
    if (cached) return cached;
    const kvt = num === 1 ? sub.kvt1_base64 : sub.kvt2_base64;
    if (!kvt) return null;
    await pickKeyForOrg(sub.organization_id);  // ключ юрособи у слот
    const xml = developReceiptToXml(kvt);
    // Кеш на сервері (XML + читабельний текст) — далі бланк без ключа.
    const f = parseReceiptFieldsFromXml(xml);
    await dpsSubmissions.saveReceiptText(
      sub.id, f.text || f.result, f.accepted, xml, num)
      .then(() => refreshJournal())
      .catch(() => {});
    return xml;
  }

  // Відкрити квитанцію №num друкованим бланком (форма J1499202).
  async function openReceiptNum(sub: DpsSubmission, num: 1 | 2) {
    setError(null);
    setBusyId(sub.id);
    try {
      const xml = await receiptXml(sub, num);
      if (!xml) { setError(`Квитанції №${num} ще немає.`); return; }
      setReceiptView({
        fields: parseReceiptFieldsFromXml(xml),
        org: orgName.get(sub.organization_id) ?? '',
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  // Розшифрувати наявні квитанції тихо (для кешу після refresh) — без попапа.
  async function cacheReceipts(sub: DpsSubmission) {
    try {
      if (sub.kvt1_base64 && !sub.receipt1_xml) await receiptXml(sub, 1);
      if (sub.kvt2_base64 && !sub.receipt2_xml) await receiptXml(sub, 2);
    } catch { /* кеш не критичний — квитанцію все одно відкриють */ }
  }

  // Друк/збереження бланка квитанції у PDF — системний діалог друку браузера
  // (лишається лише бланк завдяки .dps-printing у styles.css).
  function printReceipt() {
    document.body.classList.add('dps-printing');
    window.print();
    setTimeout(() => document.body.classList.remove('dps-printing'), 500);
  }

  // colId сортованих колонок = ключі whitelist серверного сортування
  // (created_at/title/form_code/period/org/status); «Дія»/«Квитанції» —
  // sortable:false (сервер їх не сортує).
  const cols = useMemo<ColDef<DpsSubmission>[]>(() => [
    {
      colId: 'created_at', field: 'created_at', headerName: 'Дата', width: 140,
      valueFormatter: (p) => p.value ? fmtDate(p.value) : '',
    },
    {
      colId: 'title', headerName: 'Назва', width: 280,
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) => {
        if (!r) return '';
        const route = SOURCE_ROUTE[r.source_type]?.(r.source_id);
        const label = r.form_title ?? r.form_code;
        return route
          ? <a style={{ cursor: 'pointer' }} onClick={() => navigate(route)}
              title={`${r.form_code} · ${r.file_name}`}>{label}</a>
          : <span title={r.form_code}>{label}</span>;
      },
    },
    {
      colId: 'form_code', headerName: 'Форма', width: 100,
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) =>
        r ? <span title={r.file_name}>{r.form_code}</span> : '',
    },
    {
      colId: 'period', headerName: 'Період', width: 120,
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) =>
        r ? periodLabel(r) : '',
    },
    {
      colId: 'org', headerName: 'Юрособа', width: 170,
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) =>
        r ? (orgName.get(r.organization_id) ?? '—') : '',
    },
    {
      colId: 'status', headerName: 'Статус', width: 150,
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) => {
        if (!r) return '';
        const s = subState(r);
        return (
          <span style={{ color: s.color, fontWeight: 600 }}
            title={r.error_message ?? r.dps_status_code ?? ''}>
            {s.text}
          </span>
        );
      },
    },
    {
      colId: 'act', headerName: 'Дія', width: 130, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) => {
        if (!r) return '';
        const isDraft = r.status === 'DRAFT' || r.status === 'ERROR';
        const isSigned = r.status === 'SIGNED';
        // Відхилений ДПС пакет подається ЗАНОВО (виправив → підписав →
        // відправив). Без цього після «не прийнято» у рядку не лишалось
        // жодної дії — факт 2026-08-18.
        const isRejected = r.status === 'REJECTED';
        const has2 = !!(r.kvt2_base64 || r.receipt2_xml);
        // «Оновити» (запит кв.№2) — доступно, поки подача не фіналізована.
        const needsKvt2 = (r.status === 'SENT' || r.status === 'DELIVERED') && !has2;
        const busy = busyId === r.id;
        return (
          <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
            {(isDraft || isSigned || isRejected) && (
              <IconBtn label={isRejected
                  ? 'Підписати заново для ПОВТОРНОГО подання (ДПС не прийняла)'
                  : isSigned ? 'Перепідписати ключем КЕП'
                    : 'Підписати ключем КЕП (відправка — окремо)'}
                disabled={busy || !keyReady} busy={busy}
                hint={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)' : undefined}
                onClick={() => signSub(r)}>
                <IconSign />
              </IconBtn>
            )}
            {isSigned && (
              <IconBtn label="Відправити підписаний документ у ДПС"
                color="#2563eb" disabled={busy} busy={busy}
                onClick={() => sendSub(r)}>
                <IconSend />
              </IconBtn>
            )}
            {needsKvt2 && (
              <IconBtn label="Оновити статус — запитати квитанцію №2 у ДПС"
                disabled={busy || !keyReady} busy={busy}
                hint={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)' : undefined}
                onClick={() => refresh(r)}>
                <IconRefresh />
              </IconBtn>
            )}
            {isDraft && (
              <IconBtn label={r.source_type === 'doc.tax_reports'
                  ? 'Видалити чернетку та розрахунок' : 'Видалити чернетку'}
                color="#dc2626" disabled={busy} busy={busy}
                onClick={() => removeDraft(r)}>
                <IconTrash />
              </IconBtn>
            )}
          </span>
        );
      },
    },
    {
      colId: 'kvt', headerName: 'Квитанції', width: 120, sortable: false,
      cellRenderer: ({ data: r }: { data?: DpsSubmission }) => {
        if (!r) return '';
        const has1 = !!(r.kvt1_base64 || r.receipt1_xml);
        const has2 = !!(r.kvt2_base64 || r.receipt2_xml);
        // Кеш XML квитанції заповнюється на сервері при отриманні (тіло
        // підписане відкрито) → бланк відкривається БЕЗ ключа. Ключ потрібен
        // лише для рідкісної зашифрованої квитанції (немає receiptN_xml).
        const open1 = !!r.receipt1_xml || (has1 && keyReady);
        const open2 = !!r.receipt2_xml || (has2 && keyReady);
        // «Оновити» (запит кв.№2) переїхало у колонку «Дія».
        return (
          <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center',
            flexWrap: 'wrap' }}>
            <Kvt n={1} active={has1} clickable={open1}
              busy={busyId === r.id} onClick={() => openReceiptNum(r, 1)} />
            <Kvt n={2} active={has2} clickable={open2}
              busy={busyId === r.id} onClick={() => openReceiptNum(r, 2)} />
          </span>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, orgName, busyId, keyReady, orgList.data]);

  // Колонки журналу «Отримати ПН»: період запиту + статус (шт/помилка).
  const erpnLogCols = useMemo<ColumnDef<ErpnLogRow>[]>(() => [
    {
      key: 'created_at', header: 'Коли', width: 140,
      sortValue: (r) => r.created_at, cell: (r) => fmtDate(r.created_at),
    },
    {
      key: 'period', header: 'Період', width: 190,
      sortValue: (r) => r.date_from,
      cell: (r) => `${new Date(r.date_from).toLocaleDateString('uk-UA')} — ${new Date(r.date_to).toLocaleDateString('uk-UA')}`,
    },
    {
      key: 'status', header: 'Статус', width: 220,
      sortValue: (r) => r.status,
      cell: (r) => r.status === 'OK'
        ? <span style={{ color: 'var(--badge-green-fg)' }}>отримано {r.fetched} шт</span>
        : <span style={{ color: '#dc2626' }} title={r.error_text ?? undefined}>
            помилка{r.fetched ? ` (отримано ${r.fetched} шт)` : ''}
          </span>,
    },
    {
      key: 'created', header: 'Створено', width: 90, align: 'right',
      sortValue: (r) => r.created, cell: (r) => r.created,
    },
    {
      key: 'matched', header: 'Привʼязано', width: 100, align: 'right',
      sortValue: (r) => r.matched, cell: (r) => r.matched,
    },
    {
      key: 'skipped_existing', header: 'Вже були', width: 90, align: 'right',
      sortValue: (r) => r.skipped_existing, cell: (r) => r.skipped_existing,
    },
    {
      key: 'rk_to_sign', header: 'РК на підпис', width: 100, align: 'right',
      sortValue: (r) => r.rk_to_sign ?? 0, cell: (r) => r.rk_to_sign ?? 0,
    },
    {
      key: 'issued_updated', header: 'Видані оновлено', width: 120, align: 'right',
      sortValue: (r) => r.issued_updated ?? 0, cell: (r) => r.issued_updated ?? 0,
    },
    {
      key: 'error_text', header: 'Деталі',
      cell: (r) => r.error_text
        ? <span style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem' }}>{r.error_text}</span>
        : '',
    },
  ], []);



  // Селект юрособи — спільний для всіх вкладок (у кожної свій рядок фільтрів).
  const orgSelect = (
    <select value={orgId}
      onChange={(e) => {
        setOrgId(e.target.value);
        // Зміна юрособи → скинути показані вхідні/звіти (інша юрособа = інші дані).
        setRepFiles(null);
      }}
      style={{ width: 220, flex: '0 0 auto' }}>
      <option value="">усі</option>
      {orgList.data?.items.map((o) => (
        <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
      ))}
    </select>
  );

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          Кабінет ДПС{tab === 'journal' && <TitleTotal total={journalTotal} />}
        </h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet/new')}>+ створити</button>
      </div>

      {/* 1) Авторизація ключами КЕП — бейджі зчитаних ключів (кілька юросіб);
          потрібний обирається автоматично за юрособою документа. */}
      <KeyAuthPanel onKeysChange={setKeys} />

      <div className="seg-tabs" style={{ marginBottom: '0.6rem' }}>
        <button className={tab === 'journal' ? 'active' : ''}
          onClick={() => setTab('journal')}>Журнал</button>
        <button className={tab === 'incoming' ? 'active' : ''}
          onClick={() => setTab('incoming')}>
          Вхідні{savedDocsQ.data ? ` (${savedRows.length})` : ''}
        </button>
        <button className={tab === 'reports' ? 'active' : ''}
          onClick={() => setTab('reports')}>Звітність</button>
        <button className={tab === 'erpn' ? 'active' : ''}
          onClick={() => setTab('erpn')}>ПН/РК (ЄРПН)</button>
      </div>

      {/* 2) Фільтри — свої для кожної вкладки. */}
      {tab === 'journal' && (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
          marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          {orgSelect}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: 150, flex: '0 0 auto' }}>
            <option value="">Усі статуси</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
          </select>
          <input value={titleQ} onChange={(e) => setTitleQ(e.target.value)}
            placeholder="Назва…" title="Пошук серед назв документів"
            style={{ width: 170, flex: '0 0 auto' }} />
          <input value={formQ} onChange={(e) => setFormQ(e.target.value)}
            placeholder="Форма…" title="Пошук за кодом форми (напр. J0500110)"
            style={{ width: 120, flex: '0 0 auto' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>з</span>
            <div style={{ width: 140 }}>
              <DatePopover value={dateFrom} onChange={setDateFrom} />
            </div>
            <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>по</span>
            <div style={{ width: 140 }}>
              <DatePopover value={dateTo} onChange={setDateTo} />
            </div>
            {(dateFrom || dateTo) && (
              <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
                onClick={() => { setDateFrom(''); setDateTo(''); }}>×</button>
            )}
          </div>
        </div>
      </>
      )}
      {tab === 'incoming' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginTop: 0, marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        {orgSelect}
        <button className="primary"
          style={{ padding: '0.25rem 0.7rem', fontSize: '0.82rem' }}
          disabled={privBusy || !keyReady || !orgId}
          title={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)'
            : !orgId ? 'Оберіть юрособу'
              : 'Забрати з кабінету ДПС нові вхідні (тексти зберігаються у системі; файли тягнуться при перегляді)'}
          onClick={() => orgId && syncIncoming(orgId)}>
          {privBusy ? 'оновлення…' : 'оновити'}
        </button>
        <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
          {!orgId ? 'оберіть юрособу'
            : 'вхідні з кабінету ДПС (збережені у системі): витяги, довідки, ППР, повідомлення — «оновити» забирає нові'}
        </span>
      </div>
      )}
      {/* Вкладка «Отримати ПН» (2026-08-04, рішення власника): окремо від
          «Вхідні» — період + кнопка + журнал запусків (період і статус). */}
      {tab === 'erpn' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginTop: 0, marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        {orgSelect}
        <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
          період:
        </span>
        <div style={{ width: 140 }}>
          <DatePopover value={erpnFrom} onChange={setErpnFrom} />
        </div>
        <span>—</span>
        <div style={{ width: 140 }}>
          <DatePopover value={erpnTo} onChange={setErpnTo} />
        </div>
        <button className="primary"
          style={{ padding: '0.25rem 0.7rem', fontSize: '0.82rem' }}
          disabled={erpnBusy || !orgId || !keyReady}
          title={!keyReady
            ? 'Зчитайте ключ КЕП (угорі) — логін у кабінет пройде автоматично'
            : 'Одна дія за період: 1) отримані ПН з ЄРПН → вхідні у системі; 2) непідписані РК з «Реєстрація ПН-РК» → «на підпис» у ПН/РК (вхідні); 3) статуси НАШИХ виданих ПН/РК за реєстром кабінету'}
          onClick={() => orgId && syncAll(orgId)}>
          {erpnBusy ? 'синхронізація…' : 'синхронізувати з ЄРПН'}
        </button>
        <a style={{ marginLeft: 'auto', fontSize: '0.82rem', cursor: 'pointer',
          color: 'var(--primary)' }}
          onClick={() => navigate('/tax-invoices-incoming')}>
          відкрити вхідні ПН →
        </a>
      </div>
      )}
      {tab === 'erpn' && syncReport && (
        <div style={{ background: 'var(--badge-green-bg)', border: '1px solid #bbf7d0',
          borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
          fontSize: '0.85rem' }}>
          <b>Синхронізація з ЄРПН за {erpnFrom} — {erpnTo}:</b>
          <div style={{ marginTop: '0.2rem' }}>
            1. Отримані ПН: у ЄРПН {syncReport.fetched ?? 0}, створено{' '}
            {syncReport.created ?? 0} (привʼязано до підстави{' '}
            {syncReport.matched ?? 0}), вже були {syncReport.skipped_existing ?? 0}.{' '}
            <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
              onClick={() => navigate('/tax-invoices-incoming')}>
              відкрити ПН/РК (вхідні) →
            </a>
          </div>
          <div>
            2. РК на наш підпис: у кабінеті {syncReport.b2b_total ?? 0},
            нових «на підпис» {syncReport.rk_to_sign_created ?? 0}
            {(syncReport.rk_skipped_signed ?? 0) > 0 &&
              `, вже підписані нами ${syncReport.rk_skipped_signed}`}
            {(syncReport.rk_signed_now ?? 0) > 0 &&
              `, підтверджено підпис у кабінеті ${syncReport.rk_signed_now}`}
            {(syncReport.rk_already ?? 0) > 0 &&
              `, вже у системі ${syncReport.rk_already}`}.
          </div>
          <div>
            3. Наші видані ПН/РК: статус оновлено {syncReport.issued_updated ?? 0},
            вже були зареєстровані {syncReport.already_registered ?? 0}.
          </div>
          {[['без підстави (привʼязати вручну)', syncReport.unmatched ?? []],
            ['створені постачальники (з даних ЄРПН — перевірте картки)',
              syncReport.created_contractors ?? []],
            ['постачальника немає у довіднику', syncReport.no_contractor ?? []],
            ['наші відправлені, яких НЕМАЄ у реєстрі (ще не зареєстровані)',
              syncReport.not_in_register ?? []],
            ['помилки', syncReport.errors ?? []]].map(([label, list]) =>
            (list as string[]).length > 0 && (
              <details key={label as string} style={{ marginTop: '0.25rem' }}>
                <summary>{label as string} ({(list as string[]).length})</summary>
                <ul style={{ margin: '0.25rem 0 0 1rem' }}>
                  {(list as string[]).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </details>
            ))}
        </div>
      )}

      {expiringCerts.length > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
          color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
          marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          ⚠ <b>Сертифікати ДПС спливають:</b>{' '}
          {expiringCerts.map((c) =>
            `${c.name} — ${c.expired ? 'ПРОСТРОЧЕНО' : `${c.days_left} дн.`}`)
            .join('; ')}.
          {' '}Без чинного сертифіката шифрування ДПС не прийме жоден пакет.
          Зверніться до підтримки для оновлення сертифікатів.
        </div>
      )}
      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem',
          whiteSpace: 'pre-line' }}>{error}</div>
      )}

      {tab === 'journal' && (
      <InfiniteDocGrid<DpsSubmission>
        datasource={journalDs}
        columnDefs={cols}
        storageKey="dps-cabinet-journal"
        refreshRef={journalRefreshRef}
      />
      )}

      {tab === 'erpn' && (
        !orgId ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Оберіть юрособу.
          </p>
        ) : (
        <DataTable<ErpnLogRow>
          rows={erpnLogQ.data ?? []}
          columns={erpnLogCols}
          rowKey={(r) => r.id}
          rowBold={(r) => !r.viewed_at}
          loading={erpnLogQ.isLoading}
          emptyText="Ще не отримували ПН з ЄРПН — оберіть період і натисніть «ПН → у систему»."
          initialSortKey="created_at"
          initialSortDir="desc"
        />
        )
      )}

      {tab === 'incoming' && orgId && (
        <div>
          {savedRows.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              {savedDocsQ.isLoading ? 'Завантаження…'
                : 'Збережених вхідних немає — натисніть «оновити», щоб забрати з кабінету ДПС.'}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  <th style={{ padding: '0.35rem', width: 150 }}>Дата</th>
                  <th style={{ padding: '0.35rem', width: 110 }}>Форма</th>
                  <th style={{ padding: '0.35rem' }}>Назва</th>
                  <th style={{ padding: '0.35rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {savedRows
                  .slice(savedPage * SAVED_PER_PAGE, (savedPage + 1) * SAVED_PER_PAGE)
                  .map((r) => (
                  /* 0432: жирним — непереглянуті У НАС; прапор is_read
                     ВЕБ-кабінету (●) не виводимо (рішення власника). */
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)',
                    fontWeight: r.viewed_at ? 400 : 700 }}>
                    <td style={{ padding: '0.35rem' }}>{r.date_in ?? '—'}</td>
                    <td style={{ padding: '0.35rem' }}>{r.cdoc ?? '—'}</td>
                    <td style={{ padding: '0.35rem' }}>
                      {savedName(r)}
                    </td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>
                      <button style={{ padding: '0.15rem 0.55rem', fontSize: '0.78rem' }}
                        disabled={privBusy}
                        title={!keyReady && !r.has_pdf && r.has_body !== false
                          ? 'Спершу зчитайте ключ КЕП (угорі) — або документ уже в архіві «Кабінет ДПС» (відкриється без ключа)'
                          : 'Файл тягнеться з кабінету ДПС (кеш до кінця сесії)'}
                        onClick={() => openSavedDoc(r)}>
                        {privBusy ? '…' : 'переглянути'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {savedRows.length > SAVED_PER_PAGE && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem',
              marginTop: '0.6rem', fontSize: '0.82rem' }}>
              <button style={{ padding: '0.2rem 0.6rem' }}
                disabled={savedPage === 0}
                onClick={() => setSavedPage((pg) => pg - 1)}>
                ← назад
              </button>
              <span style={{ color: 'var(--muted)' }}>
                сторінка {savedPage + 1} з {Math.ceil(savedRows.length / SAVED_PER_PAGE)}
              </span>
              <button style={{ padding: '0.2rem 0.6rem' }}
                disabled={(savedPage + 1) * SAVED_PER_PAGE >= savedRows.length}
                onClick={() => setSavedPage((pg) => pg + 1)}>
                далі →
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'reports' && (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
          marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          {orgSelect}
          <label style={{ fontSize: '0.82rem' }}>Період:</label>
          <select value={repMonth} onChange={(e) => setRepMonth(Number(e.target.value))}
            style={{ width: 130 }}>
            {['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
              'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень']
              .map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={repYear} onChange={(e) => setRepYear(Number(e.target.value))}
            style={{ width: 90 }}>
            {[0, 1, 2, 3, 4].map((d) => {
              const y = now.getFullYear() - d;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <button className="primary"
            style={{ padding: '0.25rem 0.7rem', fontSize: '0.82rem' }}
            disabled={privBusy || !keyReady || !orgId}
            title={!keyReady ? 'Спершу зчитайте ключ КЕП (угорі)'
              : !orgId ? 'Оберіть юрособу'
                : 'Забрати з кабінету ДПС звітність за обраний період (перший раз або оновити) — список зберігається у системі'}
            onClick={() => {
              if (!orgId) return;
              // Скинути кеш файлів/PDF цієї юрособи (биті кодировки тощо).
              for (const k of [...repFilesCache.current.keys()])
                if (k.startsWith(`${orgId}|`)) repFilesCache.current.delete(k);
              for (const k of [...repPdfCache.current.keys()])
                if (k.startsWith(`${orgId}|`)) repPdfCache.current.delete(k);
              setRepFiles(null);
              loadReports(orgId);
            }}>
            {privBusy ? 'оновлення…' : 'оновити'}
          </button>
        </div>
        {orgId && (
          (savedRepQ.data ?? []).length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              {savedRepQ.isLoading ? 'Завантаження…'
                : 'За цей період збереженої звітності немає — натисніть «оновити», щоб забрати з кабінету.'}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  <th style={{ padding: '0.35rem', width: 110 }}>Форма</th>
                  <th style={{ padding: '0.35rem', width: 150 }}>Дата і час</th>
                  <th style={{ padding: '0.35rem' }}>Назва</th>
                  <th style={{ padding: '0.35rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {(savedRepQ.data ?? []).map((r) => (
                  <tr key={r.codRegdoc} style={{ borderTop: '1px solid var(--border)',
                    fontWeight: r._viewed_at ? 400 : 700 }}>
                    <td style={{ padding: '0.35rem' }}>{r.doc ?? '—'}</td>
                    <td style={{ padding: '0.35rem' }}>{regDate(r)}</td>
                    <td style={{ padding: '0.35rem' }}>{regDocName(r)}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>
                      <button style={{ padding: '0.15rem 0.55rem', fontSize: '0.78rem' }}
                        disabled={privBusy}
                        title={!keyReady
                          ? 'Без ключа відкриються лише збережені в архіві «Кабінет ДПС» файли; повний список — Спершу зчитайте ключ КЕП (угорі)'
                          : undefined}
                        onClick={() => orgId && openReportFiles(r, orgId)}>
                        {privBusy ? '…' : 'файли та квитанції'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* 0434: наші подачі — тут видно і ВІДХИЛЕНІ пакети, яких у
            реєстрі кабінету немає (він показує лише прийняту звітність). */}
        {orgId && (attemptsQ.data ?? []).length > 0 && (
          <div style={{ marginTop: '1.2rem' }}>
            <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.92rem' }}>
              Наші подачі за період
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  <th style={{ padding: '0.35rem', width: 110 }}>Форма</th>
                  <th style={{ padding: '0.35rem', width: 60 }}>№</th>
                  <th style={{ padding: '0.35rem', width: 150 }}>Відправлено</th>
                  <th style={{ padding: '0.35rem', width: 150 }}>Стан</th>
                  <th style={{ padding: '0.35rem' }}>Квитанція</th>
                </tr>
              </thead>
              <tbody>
                {(attemptsQ.data ?? []).map((a) => {
                  // 5 станів (формулювання власника): надіслано до ДПС →
                  // прийнято ДПС / не прийнято ДПС (кв.№1) → звіт прийнято /
                  // звіт не прийнято (кв.№2). Стан рахує backend.
                  const bad = a.state === 'report_rejected'
                    || a.state === 'not_accepted' || a.state === 'error';
                  const good = a.state === 'report_accepted';
                  const mid = a.state === 'accepted_dps';
                  return (
                    <tr key={a.id} style={{ borderTop: '1px solid var(--border)',
                      background: bad ? 'rgba(220,38,38,0.06)' : undefined }}>
                      <td style={{ padding: '0.35rem' }}>{a.form_code}</td>
                      <td style={{ padding: '0.35rem' }}>{a.doc_num ?? '—'}</td>
                      <td style={{ padding: '0.35rem' }}>
                        {a.sent_at ? new Date(a.sent_at).toLocaleString('uk-UA') : '—'}
                      </td>
                      <td style={{ padding: '0.35rem',
                        color: bad ? '#dc2626' : good ? '#16a34a'
                          : mid ? '#0369a1' : undefined,
                        fontWeight: bad || good ? 600 : 400 }}>
                        {a.state_label ?? '—'}
                      </td>
                      <td style={{ padding: '0.35rem', color: 'var(--muted)',
                        maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' }}
                        title={a.receipt_text || a.error_message || ''}>
                        {(a.receipt_text || a.error_message || '').slice(0, 160) || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {repFiles && (
        <ModalOverlay onClose={() => setRepFiles(null)} wide>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{repFiles.title}</span>
            <button style={{ marginLeft: 'auto', padding: '0.1rem 0.5rem',
              fontSize: '0.75rem' }}
              onClick={() => setRepFiles(null)}>закрити</button>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: 0 }}>
            {repFiles.archived
              ? 'Збережені файли цього звіту (архів «Кабінет ДПС») — перегляд без ключа.'
              : 'Основний звіт, додатки та квитанції — кожен окремим файлом.'}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <tbody>
              {/* 0452: режим «з архіву» — збережені вкладення без ключа. */}
              {(repFiles.archived ?? []).map((f) => (
                <tr key={f.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.3rem' }}>{f.filename}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                    <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.78rem' }}
                      disabled={privBusy}
                      onClick={async () => {
                        try {
                          const blob = await attachments.downloadBlob(f.id);
                          const url = URL.createObjectURL(blob);
                          setCabView({ title: f.filename, pdfUrl: url });
                        } catch (e: any) {
                          setError(e?.response?.data?.detail ?? String(e));
                        }
                      }}>
                      {privBusy ? '…' : 'переглянути PDF'}
                    </button>
                  </td>
                </tr>
              ))}
              {repFiles.items.map((f) => (
                <tr key={f.codRegdoc} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.3rem' }} title={f.doc ?? undefined}>
                    {regDocName(f)}
                  </td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                    <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.78rem' }}
                      disabled={privBusy}
                      onClick={() => downloadRegDoc(f)}>
                      {privBusy ? '…' : 'переглянути PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModalOverlay>
      )}

      {cabView && (
        <ModalOverlay
          onClose={() => {
            if (cabView.pdfUrl && !cabView.keepUrl) URL.revokeObjectURL(cabView.pdfUrl);
            setCabView(null);
          }}
          wide
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{cabView.title}</span>
            {cabView.pdfUrl && (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '0.4rem' }}>
                <button
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                  title="Відкрити у повноцінному перегляді браузера (де шрифти ДПС коректні)"
                  onClick={() => window.open(cabView.pdfUrl!, '_blank')}>
                  відкрити
                </button>
                <button className="primary"
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                  title="Зберегти PDF на комп'ютер"
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = cabView.pdfUrl!;
                    a.download = `${cabView.title}.pdf`;
                    a.click();
                  }}>зберегти</button>
                <button
                  style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                  onClick={() => {
                    const f = document.getElementById(
                      'dps-doc-frame') as HTMLIFrameElement | null;
                    f?.contentWindow?.print();
                  }}>друкувати</button>
              </span>
            )}
            <button style={{ marginLeft: cabView.pdfUrl ? 0 : 'auto',
              padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}
              onClick={() => {
                if (cabView.pdfUrl && !cabView.keepUrl) URL.revokeObjectURL(cabView.pdfUrl);
                setCabView(null);
              }}>закрити</button>
          </div>
          {cabView.pdfUrl && (
            <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0 0 0.4rem' }}>
              Якщо у перегляді нижче замість тексту «####» — відкрийте документ
              кнопкою «відкрити» (там шрифти ДПС відображаються коректно).
            </p>
          )}
          {cabView.pdfUrl ? (
            <iframe id="dps-doc-frame" title={cabView.title} src={cabView.pdfUrl}
              style={{ width: '100%', height: '72vh', border: '1px solid var(--border)',
                borderRadius: 6 }} />
          ) : (
            <div style={{ fontSize: '0.9rem', lineHeight: 1.6,
              background: 'var(--bg-soft-2)', padding: '0.75rem 1rem', borderRadius: 6,
              maxHeight: '70vh', overflow: 'auto' }}
              // HTML з ДПС (стан розрахунків) — довірений відправник.
              dangerouslySetInnerHTML={{ __html: cabView.html ?? '' }} />
          )}
        </ModalOverlay>
      )}

      {receiptView && (
        <ModalOverlay onClose={() => setReceiptView(null)} wide>
          <div className="print-hide" style={{ display: 'flex', alignItems: 'center',
            gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>
              Квитанція № {receiptView.fields.num}
            </span>
            {receiptView.fields.accepted === true && (
              <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓ прийнято</span>
            )}
            {receiptView.fields.accepted === false && (
              <span style={{ color: 'var(--badge-red-fg)', fontWeight: 600 }}>✗ не прийнято</span>
            )}
            <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
              {receiptView.org}
            </span>
            <button className="primary"
              style={{ marginLeft: 'auto', padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
              onClick={printReceipt}>зберегти в PDF / друк</button>
            <button style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => setReceiptView(null)}>закрити</button>
          </div>
          <div className="dps-receipt-print-root">
            <DpsReceiptBlank f={receiptView.fields} />
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

// --- Іконки дій (inline SVG, stroke-стиль; без зовнішніх залежностей) ---
const svgProps = {
  width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.8,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
/** Підписати — перо (олівець-підпис). */
const IconSign = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
/** Відправити — паперовий літачок. */
const IconSend = () => (
  <svg {...svgProps}>
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </svg>
);
/** Оновити статус — кругова стрілка. */
const IconRefresh = () => (
  <svg {...svgProps}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);
/** Видалити — кошик. */
const IconTrash = () => (
  <svg {...svgProps}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/** Кнопка-іконка у колонці «Дія»: тільки іконка + tooltip (Закон дизайну #7
 *  забороняє емодзі у кнопках-діях; SVG-іконка з підказкою — не емодзі). */
function IconBtn({ label, hint, color, disabled, busy, onClick, children }: {
  label: string;
  hint?: string;
  color?: string;
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={hint ?? label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 26, padding: 0, border: '1px solid transparent',
        borderRadius: 6, background: 'transparent',
        color: disabled ? '#cbd5e1' : (color ?? '#475569'),
        cursor: disabled ? 'default' : 'pointer',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = '#f1f5f9';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'transparent';
      }}
    >
      {busy && disabled ? '…' : children}
    </button>
  );
}

/** Бейдж квитанції №1/№2: активний (є квитанція) → клікабельний бланк;
 *  сірий (немає) → неактивний. */
function Kvt(
  { n, active, clickable, busy, onClick }: {
    n: 1 | 2; active: boolean; clickable: boolean; busy: boolean;
    onClick: () => void;
  },
) {
  const base: React.CSSProperties = {
    padding: '0.12rem 0.45rem', fontSize: '0.72rem', borderRadius: 4,
    border: '1px solid', fontWeight: 600, lineHeight: 1.3,
  };
  if (!active) {
    return (
      <span style={{ ...base, color: '#94a3b8', borderColor: 'var(--border)',
        background: 'var(--bg-soft-2)' }} title={`Квитанції №${n} ще немає`}>
        кв.№{n}
      </span>
    );
  }
  return (
    <button style={{ ...base, color: 'var(--badge-blue-fg)', borderColor: 'var(--badge-blue-bd)',
      background: 'var(--primary-soft)', cursor: clickable ? 'pointer' : 'not-allowed',
      opacity: clickable ? 1 : 0.6 }}
      disabled={!clickable || busy}
      title={clickable ? `Відкрити квитанцію №${n} (бланк, PDF)`
        : 'Зчитайте ключ КЕП, щоб відкрити квитанцію'}
      onClick={onClick}>
      {busy ? '…' : `кв.№${n}`}
    </button>
  );
}

/**
 * Проста модалка-оверлей: затемнений фон + біла картка по центру.
 * Клік по фону / Esc — закриває. Скрол — усередині картки.
 */
function ModalOverlay(
  { children, onClose, wide = false }: {
    children: ReactNode; onClose: () => void; wide?: boolean;
  },
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="print-hide-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: wide ? 900 : 720, width: '100%', maxHeight: '85vh',
          overflow: 'auto', background: 'var(--bg-panel)', borderRadius: 8,
          padding: '1rem 1.25rem', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
